import { useMemo, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, AlertCircle, CalendarClock, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react'
import { format, startOfMonth, endOfMonth, addMonths, getDate, getDay } from 'date-fns'
import { it } from 'date-fns/locale'
import { getInOutGrid, type InOutTotal } from '../../lib/api/client'
import { InOutDayModal } from './InOutDayModal'
import { cn } from '../../lib/cn'

const DOW = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
const cap1 = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="cv-panel px-4 py-3.5">
      <div className="text-[11px] font-bold uppercase tracking-[1.4px] text-muted">{label}</div>
      <div className={cn('mt-1 font-display text-[24px] font-bold leading-none tracking-[-.5px]', tone ?? 'text-coal')}>{value}</div>
      {sub && <div className="mt-1 text-[12px] text-muted">{sub}</div>}
    </div>
  )
}

export function InOutPage() {
  const [anchor, setAnchor] = useState(() => startOfMonth(new Date()))
  const [modalDate, setModalDate] = useState<string | null>(null)

  const monthStart = anchor
  const monthEnd = endOfMonth(anchor)
  const startStr = format(monthStart, 'yyyy-MM-dd')
  const endStr = format(monthEnd, 'yyyy-MM-dd')
  const todayStr = format(new Date(), 'yyyy-MM-dd')

  const grid = useQuery({
    queryKey: ['inOutGrid', startStr, endStr],
    queryFn: () => getInOutGrid(startStr, endStr),
    placeholderData: keepPreviousData,
  })
  const byDay = useMemo(() => {
    const m: Record<string, InOutTotal> = {}
    for (const t of grid.data?.total ?? []) m[t.start] = t
    return m
  }, [grid.data])

  const dStr = (d: number) => format(new Date(anchor.getFullYear(), anchor.getMonth(), d), 'yyyy-MM-dd')
  const daysInMonth = getDate(monthEnd)
  const lead = (getDay(monthStart) + 6) % 7
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth])

  const stats = useMemo(() => {
    let inSum = 0
    let outSum = 0
    let peak = 0
    let presSum = 0
    let withData = 0
    for (const d of days) {
      const t = byDay[dStr(d)]
      if (!t) continue
      inSum += t.in
      outSum += t.out
      peak = Math.max(peak, t.total)
      presSum += t.total
      withData++
    }
    return { inSum, outSum, peak, avg: withData ? Math.round(presSum / withData) : 0 }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byDay, anchor, days])

  const monthLabel = cap1(format(anchor, 'LLLL yyyy', { locale: it }))
  const isCurrentMonth = startStr === format(startOfMonth(new Date()), 'yyyy-MM-dd')

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      {/* page-head */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <h2 className="font-display text-[26px] font-bold leading-tight tracking-[-.3px] text-coal sm:text-[28px]">In &amp; Out</h2>
          <p className="mt-1.5 max-w-[70ch] text-[13.5px] text-muted">
            Arrivi, partenze e veicoli in sosta giorno per giorno. Clicca un giorno per la plancia operativa con gli
            elenchi e l&apos;esportazione.
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2.5 sm:ml-auto sm:w-auto">
          <div className="ml-auto flex items-center gap-1 rounded-lg border border-line bg-card p-1 sm:ml-0">
            <button onClick={() => setAnchor((a) => addMonths(a, -1))} className="rounded-md px-1.5 py-1 text-muted hover:bg-paper hover:text-coal" title="Mese precedente">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[120px] px-1 text-center text-[13px] font-semibold text-coal">{monthLabel}</span>
            <button onClick={() => setAnchor((a) => addMonths(a, 1))} className="rounded-md px-1.5 py-1 text-muted hover:bg-paper hover:text-coal" title="Mese successivo">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {!isCurrentMonth && (
            <button
              onClick={() => setAnchor(startOfMonth(new Date()))}
              className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-card px-3 py-[9px] text-[13px] font-semibold text-slate hover:border-faint"
            >
              <CalendarClock className="h-4 w-4" /> Oggi
            </button>
          )}
        </div>
      </div>

      {/* stat cards */}
      <div className="mb-[18px] grid grid-cols-2 gap-[14px] lg:grid-cols-4">
        <Stat label="Arrivi (mese)" value={stats.inSum.toLocaleString('it-IT')} sub="ingressi totali" tone="text-ok" />
        <Stat label="Partenze (mese)" value={stats.outSum.toLocaleString('it-IT')} sub="uscite totali" tone="text-canal" />
        <Stat label="Picco presenze" value={stats.peak.toLocaleString('it-IT')} sub="massimo in sosta" />
        <Stat label="Media presenze" value={stats.avg.toLocaleString('it-IT')} sub="al giorno" />
      </div>

      {/* calendar */}
      <div className="cv-panel p-4 sm:p-5">
        {grid.isError ? (
          <div className="px-4 py-16 text-center text-sm text-muted">
            <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare gli ingressi/uscite.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              <div className="grid grid-cols-7 gap-1.5">
                {DOW.map((d) => (
                  <div key={d} className="pb-1 text-center text-[11px] font-bold uppercase tracking-[1px] text-muted">
                    {d}
                  </div>
                ))}
                {Array.from({ length: lead }).map((_, i) => (
                  <div key={`lead-${i}`} className="min-h-[120px] rounded-lg bg-paper/40" />
                ))}
                {days.map((d) => {
                  const ds = dStr(d)
                  const t = byDay[ds]
                  const isToday = ds === todayStr
                  return (
                    <button
                      key={d}
                      onClick={() => setModalDate(ds)}
                      className="flex min-h-[120px] flex-col overflow-hidden rounded-lg border border-line bg-card text-left transition-colors hover:border-faint"
                    >
                      <div className={cn('flex items-center justify-between px-2.5 py-1.5', isToday ? 'bg-coal text-white' : 'bg-black/[0.03] text-coal')}>
                        {isToday ? <span className="text-[10px] font-bold uppercase tracking-wider">oggi</span> : <span />}
                        <span className="text-[15px] font-bold">{d}</span>
                      </div>
                      {grid.isLoading ? (
                        <div className="flex-1 p-2.5"><div className="h-4 w-14 rounded bg-line-soft" /></div>
                      ) : t ? (
                        <div className="flex flex-1 flex-col p-2.5">
                          <div className="font-mono text-[22px] font-bold leading-none tnum text-coal">
                            {t.total}
                            <span className="ml-1 text-[11px] font-semibold text-muted">in sosta</span>
                          </div>
                          <div className="mt-auto flex items-center justify-between gap-1 border-t border-line-soft pt-2 text-[13px] font-semibold">
                            <span className={cn('flex items-center gap-1', t.in > 0 ? 'text-ok' : 'text-faint')}>
                              <ArrowDownToLine className="h-4 w-4" /> +{t.in}
                            </span>
                            <span className={cn('flex items-center gap-1', t.out > 0 ? 'text-canal' : 'text-faint')}>
                              <ArrowUpFromLine className="h-4 w-4" /> −{t.out}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-1 items-center justify-center text-[11px] text-faint">—</div>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* legend */}
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-muted">
          <span className="flex items-center gap-1 font-medium text-ok"><ArrowDownToLine className="h-3.5 w-3.5" /> arrivi</span>
          <span className="flex items-center gap-1 font-medium text-canal"><ArrowUpFromLine className="h-3.5 w-3.5" /> partenze</span>
          <span>“in sosta” = veicoli presenti in garage quel giorno</span>
        </div>
      </div>

      <InOutDayModal date={modalDate} onClose={() => setModalDate(null)} onNavigate={(d) => setModalDate(d)} />
    </div>
  )
}
